'use client'

import { useEffect, useRef } from 'react'
import { track } from '@/utils/analytics'
import PreferredSourceButton from '@/app/components/PreferredSourceButton'

// Small, evidence-based pilot rather than a sitewide in-article interruption.
// These three posts overlap the highest blog pages in the saved GSC and GA4
// exports, giving the experiment enough traffic to produce a useful signal.
const PILOT_SLUGS = new Set([
  'microplastics-in-drinking-water-by-country',
  'microplastics-in-canned-food',
  'microplastics-in-k-cups-and-coffee-pods',
])

export default function PreferredSourcesArticleCard({
  postSlug,
  postTopic,
}: {
  postSlug: string
  postTopic: string
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  const hasTrackedView = useRef(false)

  useEffect(() => {
    const element = cardRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || hasTrackedView.current) return
        hasTrackedView.current = true
        track('preferred_source_badge_impression', {
          cta_surface: 'blog_after_toc_pilot',
          page_type: 'blog',
          post_slug: postSlug,
          post_topic: postTopic,
        })
        observer.disconnect()
      },
      { threshold: 0.5 }
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [postSlug, postTopic])

  if (!PILOT_SLUGS.has(postSlug)) return null

  return (
    <aside
      ref={cardRef}
      aria-label="Follow MicroPlastics in Google Search"
      className="not-prose mb-6"
    >
      <PreferredSourceButton
        onClick={() =>
          track('preferred_source_badge_click', {
            cta_surface: 'blog_after_toc_pilot',
            page_type: 'blog',
            post_slug: postSlug,
            post_topic: postTopic,
            destination:
              'https://www.google.com/preferences/source?q=microplasticsapp.com',
            external_completion_required: true,
          })
        }
      />
    </aside>
  )
}
